import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';

//Arquivos do ngrx
import { Store } from '@ngrx/store';
import { LoginPageActions } from '../../../actions-index';
import { getError, getLoading, getMfaToken } from '../../../state';
//Services
import { LoginService } from 'src/shared/services/login.service';
//Others
import { Observable } from 'rxjs';

// Imports standalone
import { InputOtp } from 'primeng/inputotp';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.less'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    InputOtp,
  ],
})
export class LoginComponent implements OnInit {
  public inputUser = new FormControl('', Validators.required);
  public inputPassword = new FormControl('', Validators.required);

  /** Segunda etapa: código de 6 dígitos do app **/
  public inputMfaCode = new FormControl('', [
    Validators.required,
    Validators.pattern(/^\d{6}$/),
  ]);
  /** Segunda etapa: código de recuperação no formato XXXXX-XXXXX **/
  public inputRecoveryCode = new FormControl('', [
    Validators.required,
    Validators.pattern(/^[A-Za-z0-9]{10}$/),
  ]);

  public loading$: Observable<boolean>;
  public error$: Observable<string | null>;
  /** Tem valor quando o Bifrost exigiu 2FA; é o que liga a etapa 2 na tela **/
  public mfaToken$: Observable<string | null>;
  public isShowingPassword: boolean = false;
  /** true = usa o código de recuperação no lugar do código do app **/
  public useRecoveryCode: boolean = false;

  /** Armazena a mensagem de erro ao tentar realizar o login **/
  public loginErrorMessage: string | undefined;

  constructor(
    private store: Store,
    private loginService: LoginService,
    private cdr: ChangeDetectorRef,
  ) {
    this.error$ = store.select(getError);
    this.loading$ = store.select(getLoading);
    this.mfaToken$ = store.select(getMfaToken);

    /** Se inscreve no subject que contém a mensagem de erro retornada so service de login **/
    this.loginService.messageErrorSubject$.subscribe((value) => {
      /** Recebe a mensagem de erro já convertida **/
      this.loginErrorMessage = value;
    });

    /** Rede de segurança: qualquer valor que entre no campo (teclado de celular,
     *  arrastar e soltar etc.) é limpo para só letras e números **/
    this.inputRecoveryCode.valueChanges.subscribe((value) => {
      const clean = this.sanitizeRecoveryCode(value ?? '');
      if (clean !== value) {
        this.inputRecoveryCode.setValue(clean, { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.store.dispatch(LoginPageActions.enter());
    this.loginService.showSideBar.emit(false);
    this.loginService.showSideBarUpdateUser.emit(false);
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('username');
    localStorage.removeItem('expiredPassword');
    localStorage.removeItem('geoserverPassword');
    localStorage.removeItem('selectedInstallations');
  }

  async login() {
    this.loginErrorMessage = undefined;

    this.store.dispatch(
      LoginPageActions.login({
        login: {
          username: this.inputUser.value,
          password: this.inputPassword.value,
        },
      }),
    );
  }

   // ---------- Código de recuperação: só letras e números ----------

  private recoveryOtpEl?: HTMLElement;

  /** O p-inputotp do código de recuperação só existe quando o checkbox está marcado,
   *  então o listener é ligado e desligado aqui, conforme ele aparece e some **/
  @ViewChild('recoveryOtp', { read: ElementRef })
  set recoveryOtp(ref: ElementRef<HTMLElement> | undefined) {
    this.recoveryOtpEl?.removeEventListener('paste', this.onRecoveryPaste, true);
    this.recoveryOtpEl = ref?.nativeElement;
    // true = fase de captura: roda antes do tratamento de colar do próprio InputOtp
    this.recoveryOtpEl?.addEventListener('paste', this.onRecoveryPaste, true);
  }

  /** Ctrl+V: pega só o código, sem hífen, espaços ou texto em volta **/
  private readonly onRecoveryPaste = (event: ClipboardEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    const text = event.clipboardData?.getData('text') ?? '';
    this.inputRecoveryCode.setValue(this.sanitizeRecoveryCode(text));
    this.inputRecoveryCode.markAsDirty();
    this.cdr.markForCheck();
  };

  /** Digitação: bloqueia hífen, espaço e qualquer caractere que não seja letra ou número **/
  onRecoveryKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return; // Ctrl+V, Ctrl+A etc. continuam funcionando
    }
    if (event.key.length === 1 && !/[A-Za-z0-9]/.test(event.key)) {
      event.preventDefault();
    }
  }

  /** "Código: ABCDE-FGHIJ" -> "ABCDEFGHIJ". Se achar o formato XXXXX-XXXXX no texto,
   *  usa só ele; senão remove tudo que não for letra ou número. Máximo de 10 caracteres **/
  private sanitizeRecoveryCode(value: string): string {
    const match = value.match(/\b[A-Za-z0-9]{5}-[A-Za-z0-9]{5}\b/);
    const source = match ? match[0] : value;
    return source
      .replace(/[^A-Za-z0-9]/g, '')
      .toUpperCase()
      .slice(0, 10);
  }

  /** Envia o código da etapa 2 (app ou recuperação, conforme o checkbox) **/
  loginMfa() {
    if (this.useRecoveryCode) {
      if (this.inputRecoveryCode.invalid) {
        return;
      }
      this.loginErrorMessage = undefined;
      const raw = this.inputRecoveryCode.value!.trim().toUpperCase();
      this.store.dispatch(
        LoginPageActions.loginMfa({
          recoveryCode: `${raw.slice(0, 5)}-${raw.slice(5)}`,
        }),
      );
    } else {
      if (this.inputMfaCode.invalid) {
        return;
      }
      this.loginErrorMessage = undefined;
      this.store.dispatch(
        LoginPageActions.loginMfa({
          code: this.inputMfaCode.value!,
        }),
      );
    }
  }

  /** Alterna entre o código do app e o código de recuperação **/
  toggleRecoveryCode(event: Event) {
    this.useRecoveryCode = (event.target as HTMLInputElement).checked;
    this.inputMfaCode.reset('');
    this.inputRecoveryCode.reset('');
    this.loginErrorMessage = undefined;
  }

  /** "Voltar": descarta o token intermediário e retorna para usuário e senha **/
  backToLogin() {
    this.loginErrorMessage = undefined;
    this.useRecoveryCode = false;
    this.inputMfaCode.reset('');
    this.inputRecoveryCode.reset('');
    this.inputPassword.reset('');
    this.store.dispatch(LoginPageActions.mfaCancel());
  }

  showPassword() {
    this.isShowingPassword = !this.isShowingPassword;
  }
}
